import { z } from 'zod';

import {
    NavigationLayoutTypes,
    SectionBackgroundColorTypes,
    SectionButtonTypes as SectionButtonTypesConst,
    SectionLayouts as SectionLayoutsConst,
    SectionTypes as SectionTypesConst,
} from '@@routes/placement/constants';

import { Entity, RichText, UnityRichText } from '../../utils/schemas/schemas';

export const Preset = Entity.extend({
    id: z.string().uuid(),
    presetName: z.string(),
    items: z.array(z.string()),
});

export type Preset = z.infer<typeof Preset>;

export const Bookmark = Entity.extend({
    id: z.string().uuid().optional(),
    metadataId: z.number(),
    relatedResourceId: z.string(),
    relatedResourceName: z.string(),
});

export type Bookmark = z.infer<typeof Bookmark>;

const SectionType = z.nativeEnum(SectionTypesConst);

export type SectionType = z.infer<typeof SectionType>;

export const SectionLayout = z.object({
    name: z.string(),
});

export type SectionLayout = z.infer<typeof SectionLayout>;

const SectionLayouts = z.nativeEnum(SectionLayoutsConst);

type SectionLayouts = z.infer<typeof SectionLayouts>;

const BaseSection = Entity.extend({
    id: z.number(),
    type: SectionType,

    /**
     * @deprecated Remove when BE removed
     */
    tenantId: z.number().nullish(),

    /**
     * @deprecated Remove when BE removed
     */
    selfCategoryId: z.number().nullish(),
    title: z.string(),
    showTitle: z.boolean(),
    isSectionVisible: z.boolean(),
});

const SectionButtonBase = z.object({
    text: z.string(),
});

type SectionButtonBase = z.infer<typeof SectionButtonBase>;

const SectionCategoryButton = SectionButtonBase.extend({
    type: z.literal(SectionButtonTypesConst.CATEGORY),
    categoryId: z.number(),
});

const SectionTagButton = SectionButtonBase.extend({
    type: z.literal(SectionButtonTypesConst.TAG),
    tagId: z.number(),
});

const SectionInternalButton = SectionButtonBase.extend({
    type: z.literal(SectionButtonTypesConst.INTERNAL),
    metadataId: z.number(),
});

const SectionExternalButton = SectionButtonBase.extend({
    type: z.literal(SectionButtonTypesConst.EXTERNAL),
    url: z.string(),
});

const SectionTextItemButton = SectionButtonBase.extend({
    type: z.literal(SectionButtonTypesConst.TEXT_ITEM),
    text: z.string(),
});

export type SectionCategoryButton = z.infer<typeof SectionCategoryButton>;
export type SectionTagButton = z.infer<typeof SectionTagButton>;
type SectionInternalButton = z.infer<typeof SectionInternalButton>;
type SectionExternalButton = z.infer<typeof SectionExternalButton>;
type SectionTextItemButton = z.infer<typeof SectionTextItemButton>;

const SectionButton = z.union([
    SectionCategoryButton,
    SectionTagButton,
    SectionInternalButton,
    SectionExternalButton,
    SectionTextItemButton,
]);

export type SectionButton = z.infer<typeof SectionButton>;

const DefaultSectionBase = BaseSection.extend({
    layout: SectionLayouts,
    teaserCount: z.number(),
    isInfiniteScroll: z.boolean().nullable(),
    adsPositions: z
        .object({
            desktop: z.array(z.number()).nullable(),
        })
        .nullish(),
    isButtonVisible: z.boolean(),
    button: SectionButton.nullable(),
    backgroundColor: z.nativeEnum(SectionBackgroundColorTypes),
});

export const DefaultCategorySection = DefaultSectionBase.extend({
    type: z.literal(SectionTypesConst.DEFAULT_CATEGORY),
});

export type DefaultCategorySection = z.infer<typeof DefaultCategorySection>;

export const DefaultTagSection = DefaultSectionBase.extend({
    type: z.literal(SectionTypesConst.DEFAULT_TAG),
});

export type DefaultTagSection = z.infer<typeof DefaultTagSection>;

export const CategorySection = DefaultSectionBase.omit({ isInfiniteScroll: true }).extend({
    type: z.literal(SectionTypesConst.CATEGORY),
    pointerCategoryId: z.number(),
    pointerSectionId: z.number().nullish(),
});

export type CategorySection = z.infer<typeof CategorySection>;

export const TagSection = DefaultSectionBase.omit({ isInfiniteScroll: true }).extend({
    type: z.literal(SectionTypesConst.TAG),
    pointerTagId: z.number(),
    pointerSectionId: z.number().nullish(),
});

export type TagSection = z.infer<typeof TagSection>;

export const CustomSection = BaseSection.extend({
    type: z.literal(SectionTypesConst.CUSTOM),
    customTypeIdentifier: z.string(),
});

export type CustomSection = z.infer<typeof CustomSection>;

export const UserCategorySelectionSection = DefaultSectionBase.omit({
    isInfiniteScroll: true,
}).extend({
    type: z.literal(SectionTypesConst.USER_CATEGORY_SELECTION),
    selectableCategoryIds: z.array(z.number()),
    defaultCategoryId: z.number(),
    navigationLayout: z.union([
        z.literal(NavigationLayoutTypes.DROPDOWN),
        z.literal(NavigationLayoutTypes.LINK),
    ]),
    subtitle: z.string(),
    pointerSectionId: z.number().nullish(),
});

export type UserCategorySelectionSection = z.infer<typeof UserCategorySelectionSection>;

export const NoLogicSection = DefaultSectionBase.omit({
    isInfiniteScroll: true,
}).extend({
    type: z.literal(SectionTypesConst.NO_LOGIC),
});

export type NoLogicSection = z.infer<typeof NoLogicSection>;

export const TaPlacementEngineSection = DefaultSectionBase.omit({
    isInfiniteScroll: true,
}).extend({
    type: z.literal(SectionTypesConst.TA_PLACEMENT_ENGINE),
    selectableCategoryIds: z.array(z.number()),
    searchText: z.string(),
    weightPerformance: z.number().min(0).max(1),
    weightRecency: z.number().min(0).max(1),
    weightMomentum: z.number().min(0).max(1),
    weightSemantic: z.number().min(0).max(1),
    freshPin: z.boolean(),
});

export type TaPlacementEngineSection = z.infer<typeof TaPlacementEngineSection>;

export const ExternalWidgetSection = DefaultSectionBase.omit({
    isInfiniteScroll: true,
    isButtonVisible: true,
    button: true,
}).extend({
    type: z.literal(SectionTypesConst.EXTERNAL_WIDGET),
    widgetId: z.string(),
});

export type ExternalWidgetSection = z.infer<typeof ExternalWidgetSection>;

const ContentBlockSectionBase = BaseSection.extend({
    type: z.literal(SectionTypesConst.CONTENT_BLOCK),
});

export const UnityContentBlockSection = ContentBlockSectionBase.extend({
    content: UnityRichText.nullable(),
});

export type UnityContentBlockSection = z.infer<typeof UnityContentBlockSection>;

export const ContentBlockSection = ContentBlockSectionBase.extend({
    content: RichText.nullable(),
});

export type ContentBlockSection = z.infer<typeof ContentBlockSection>;

export const UnitySection = z.union([
    DefaultCategorySection,
    DefaultTagSection,
    CategorySection,
    CustomSection,
    TagSection,
    UserCategorySelectionSection,
    NoLogicSection,
    TaPlacementEngineSection,
    ExternalWidgetSection,
    UnityContentBlockSection,
]);

export type UnitySection = z.infer<typeof UnitySection>;

export const Section = z.union([
    DefaultCategorySection,
    DefaultTagSection,
    CategorySection,
    CustomSection,
    TagSection,
    UserCategorySelectionSection,
    NoLogicSection,
    TaPlacementEngineSection,
    ExternalWidgetSection,
    ContentBlockSection,
]);

export type Section = z.infer<typeof Section>;

export const isDefaultCategorySection = (
    section?: Section | Pick<Section, 'type'>,
): section is DefaultCategorySection => section?.type === SectionTypesConst.DEFAULT_CATEGORY;

export const isDefaultTagSection = (
    section?: Section | Pick<Section, 'type'>,
): section is DefaultTagSection => section?.type === SectionTypesConst.DEFAULT_TAG;

export const isContentBlockSection = (
    section?: Section | UnitySection | Pick<Section, 'type'>,
): section is ContentBlockSection => section?.type === SectionTypesConst.CONTENT_BLOCK;

export const isTagPageSectionType = (section: Pick<Section, 'type'>): section is TagPageSection =>
    isDefaultTagSection(section) || isContentBlockSection(section);

const UnityCategoryPageSection = z.union([
    DefaultCategorySection,
    CategorySection,
    CustomSection,
    TagSection,
    UserCategorySelectionSection,
    NoLogicSection,
    TaPlacementEngineSection,
    ExternalWidgetSection,
    UnityContentBlockSection,
]);

const CategoryPageSection = z.union([
    DefaultCategorySection,
    CategorySection,
    CustomSection,
    TagSection,
    UserCategorySelectionSection,
    NoLogicSection,
    TaPlacementEngineSection,
    ExternalWidgetSection,
    ContentBlockSection,
]);

export type CategoryPageSection = z.infer<typeof CategoryPageSection>;

export const UnityCategoryPage = z.object({
    id: z.number(),
    categoryId: z.number(),
    tenantId: z.number(),
    sections: z.array(UnityCategoryPageSection),
    draft: z.boolean(),
    allowAbTest: z.boolean().optional(),
});

export type UnityCategoryPage = z.infer<typeof UnityCategoryPage>;

export type CategoryPage = Omit<UnityCategoryPage, 'sections'> & {
    sections: CategoryPageSection[];
};

const UnityTagPageSection = z.union([DefaultTagSection, UnityContentBlockSection]);

const TagPageSection = z.union([DefaultTagSection, ContentBlockSection]);

export type TagPageSection = z.infer<typeof TagPageSection>;

export const UnityTagPage = z.object({
    id: z.number(),
    tagId: z.number(),
    tenantId: z.number(),
    sections: z.array(UnityTagPageSection),
    draft: z.boolean(),
});

export type UnityTagPage = z.infer<typeof UnityTagPage>;

export type TagPage = Omit<UnityTagPage, 'sections'> & {
    sections: TagPageSection[];
};

const SectionContentItem = z.object({
    metadataId: z.number().nullish(),
    sectionContentItemType: z.string(),
    placeholderTenantId: z.number().nullish(),
    placeholderCategoryId: z.number().nullish(),
    placeholderIndexPosition: z.number().nullish(),
    publishedSince: z.string().optional(),
    pinned: z.boolean().optional(),
    deletedDate: z.string().optional(),
    unpublishedDate: z.string().optional(),
    placeholderType: z.string().nullish(),
});

export type SectionContentItem = z.infer<typeof SectionContentItem>;

export const SectionContent = z.object({
    sectionId: z.number(),

    /**
     * @deprecated Remove when BE removed
     */
    tenantId: z.number().nullish(),

    /**
     * @deprecated Remove when BE removed
     */
    categoryId: z.number().nullish(),
    content: z.array(SectionContentItem),
    updatedAt: z.string(),
    lastUpdatedBy: z.string().nullable(),
    draft: z.boolean(),
    previewUrl: z.string().nullable(),
});

export type SectionContent = z.infer<typeof SectionContent>;

export const CopySectionParams = z.object({
    sourceSectionId: z.number(),
    targetPageId: z.string(),
    targetPosition: z.number(),
});

export type CopySectionParams = z.infer<typeof CopySectionParams>;

export const CopySectionWarning = z.enum([
    'REMOVED_PLACEHOLDERS',
    'REMOVED_CONTENT',
    'REMOVED_BUTTON',
]);

export type CopySectionWarning = z.infer<typeof CopySectionWarning>;
